// Other documents sample data (spec 406:9505). No document store exists yet: everything lives in page state.

export type FolderSort = 'newest' | 'manual';

export const SORT_LABELS: Record<FolderSort, string> = { newest: 'Newest first', manual: 'The order I choose' };

export interface Version {
    n: number;
    date: string;
}

export interface Doc {
    id: string;
    folderId: string;
    // the name visitors see
    name: string;
    // the uploaded file's own name
    file: string;
    kind: string;
    size: string;
    uploaded: string;
    uploadedBy: string;
    live: boolean;
    description: string;
    // newest first; [0] is the current file
    versions: Version[];
}

export interface Folder {
    id: string;
    name: string;
    description: string;
    // the page it is placed on: {host}/{path}/
    path: string;
    sort: FolderSort;
}

// "ESG & sustainability" → "esg-sustainability" (the shortcode's folder attribute)
export const folderSlug = (name: string) => name.toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const shortcode = (f: Folder) => `[investi-documents folder="${folderSlug(f.name)}"]`;

export const FOLDERS: Folder[] = [
    { id: 'esg', name: 'ESG & sustainability', description: 'Our environmental, social and governance reports and policies.', path: 'esg', sort: 'newest' },
    { id: 'community', name: 'Community newsletters', description: 'Quarterly updates for the communities near our projects.', path: 'community', sort: 'newest' },
    { id: 'approvals', name: 'Approvals & licences', description: 'Permits and licences granted for our projects.', path: 'approvals', sort: 'manual' },
    { id: 'annual', name: 'Annual reviews', description: 'Yearly environmental performance reports lodged with the regulator', path: 'annual-reviews', sort: 'newest' },
];

const doc = (id: string, folderId: string, name: string, file: string, size: string, uploaded: string, live: boolean, description = '', versions: Version[] = [{ n: 1, date: uploaded }]): Doc =>
    ({ id, folderId, name, file, kind: 'PDF', size, uploaded, uploadedBy: 'Wallace Turner', live, description, versions });

export const DOCS: Doc[] = [
    doc('1', 'esg', 'Sustainability Report 2025', '2025-Sustainability-Report.pdf', '4.2 MB', '21 Aug 2026', true, 'Our third annual sustainability and community report', [{ n: 2, date: '21 Aug 2026' }, { n: 1, date: '22 Sep 2025' }]),
    doc('2', 'esg', 'Environmental Policy', 'Environmental-Policy.pdf', '180 KB', '3 Jun 2026', true),
    doc('3', 'esg', 'Water Management Plan — Gold Mountain', 'Water-Management-Plan-Gold-Mountain.pdf', '2.1 MB', '3 Jun 2026', true),
    doc('4', 'esg', 'Community Engagement Framework', 'Community-Engagement-Framework.pdf', '640 KB', '14 Feb 2026', false),
    doc('5', 'community', 'Winter 2026 Community Newsletter', '2026-Community-Newsletter-Winter.pdf', '1.6 MB', '12 Jul 2026', true),
    doc('6', 'community', 'Autumn 2026 Community Newsletter', '2026-Community-Newsletter-Autumn.pdf', '1.4 MB', '8 Apr 2026', true),
    doc('7', 'community', 'Summer 2026 Community Newsletter', '2026-Community-Newsletter-Summer.pdf', '1.5 MB', '15 Jan 2026', true),
    doc('8', 'approvals', 'Buffalo Canyon Drilling Permit', 'Buffalo-Canyon-Drilling-Permit.pdf', '820 KB', '14 Aug 2026', true),
    doc('9', 'approvals', 'Gold Mountain Exploration Licence', 'Gold-Mountain-Exploration-Licence.pdf', '1.1 MB', '2 Mar 2026', true),
];

// "4.2 MB" from a File
export const fileSize = (bytes: number) => bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// "PDF" from "report.pdf"
export const fileKind = (name: string) => {
    const ext = (name.split('.').pop() ?? '').toLowerCase();
    if (ext === 'doc' || ext === 'docx') return 'Word';
    if (ext === 'xls' || ext === 'xlsx') return 'Excel';
    return ext.toUpperCase() || 'File';
};

// today as the list prints it: "16 Oct 2026"
export const today = () => new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx';
